import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './App.css';

// Context Providers
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';

// Layout Components
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import FloatingButtons from './components/layout/FloatingButtons';
import ScrollToTop from './components/common/ScrollToTop';

// Public Pages
import About from './pages/public/About';
import WorkAreas from './pages/public/WorkAreas';
import Announcements from './pages/Announcements';
import AnnouncementDetail from './pages/AnnouncementDetail';
import Gallery from './pages/Gallery';
import FAQ from './pages/FAQ';
import SiteMap from './pages/SiteMap';

// Auth Pages
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import AccountSettings from './pages/AccountSettings';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AnnouncementManagement from './pages/admin/AnnouncementManagement';
import GalleryManagement from './pages/admin/GalleryManagement';
import FAQManagement from './pages/admin/FAQManagement';
import UserManagement from './pages/admin/UserManagement';

// Protected Route Component
import ProtectedRoute from './components/ProtectedRoute';

/**
 * Ana Uygulama Component'i
 *
 * Yapı:
 * - ThemeProvider: Tema yönetimi (dark/light mode)
 * - AuthProvider: Kullanıcı authentication yönetimi
 * - BrowserRouter: Client-side routing
 * - Layout: Navbar + Pages + Footer + FloatingButtons
 */
const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <div className="app-root">
            {/* NAVBAR - Tüm sayfalarda sabit */}
            <Navbar />

            {/* ROUTES - Sayfa içerikleri */}
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<About />} />
              <Route path="/calisma-alanlarimiz" element={<WorkAreas />} />
              <Route path="/announcements" element={<Announcements />} />
              <Route path="/announcements/:slug" element={<AnnouncementDetail />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/sitemap" element={<SiteMap />} />

              {/* Auth Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />

              {/* Protected User Routes */}
              <Route
                path="/account"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'lawyer', 'user']}>
                    <AccountSettings />
                  </ProtectedRoute>
                }
              />

              {/* Protected Admin Routes */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requireAdminPassword={true}>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/announcements"
                element={
                  <ProtectedRoute requireAdminPassword={true}>
                    <AnnouncementManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/gallery"
                element={
                  <ProtectedRoute requireAdminPassword={true}>
                    <GalleryManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/faqs"
                element={
                  <ProtectedRoute requireAdminPassword={true}>
                    <FAQManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute requireAdminPassword={true}>
                    <UserManagement />
                  </ProtectedRoute>
                }
              />
            </Routes>

            {/* FLOATING BUTTONS - Sağ alt köşede sabit */}
            <FloatingButtons />

            {/* FOOTER - Tüm sayfalarda sabit */}
            <Footer />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
